import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import { Modal } from "components/Modal/Modal";
import { LoyaltyRewardTranslation } from "store/liveOps/loyalty/levels/rewards";

import styles from "./LoyaltyProgramLevelsRewardsTab.module.scss";

interface Props {
  locales: string[];
  initialTranslations: LoyaltyRewardTranslation[];
  initialIsAvailable?: boolean;
  onClose: () => void;
  onSave: (
    translations: LoyaltyRewardTranslation[],
    isAvailable: boolean,
  ) => void;
}

const emptyTranslation = (locale: string): LoyaltyRewardTranslation => ({
  locale,
  title: "",
  description: "",
});

const LoyaltyRewardInfoModal: React.FC<Props> = ({
  locales,
  initialTranslations,
  initialIsAvailable = true,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const availableLocales = useMemo(() => {
    const configuredLocales = Array.from(
      new Set([
        ...locales,
        ...initialTranslations.map((translation) => translation.locale),
      ]),
    );

    return configuredLocales.length > 0 ? configuredLocales : ["en-US"];
  }, [initialTranslations, locales]);
  const [activeLocale, setActiveLocale] = useState(
    initialTranslations[0]?.locale ?? availableLocales[0] ?? "en-US",
  );
  const [translations, setTranslations] = useState<
    Record<string, LoyaltyRewardTranslation>
  >(() =>
    Object.fromEntries(
      initialTranslations.map((translation) => [
        translation.locale,
        { ...translation },
      ]),
    ),
  );
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [isAvailable, setIsAvailable] = useState(initialIsAvailable !== false);

  const activeTranslation =
    translations[activeLocale] ?? emptyTranslation(activeLocale);
  const configuredTranslations = availableLocales
    .map((locale) => translations[locale] ?? emptyTranslation(locale))
    .filter(
      (translation) =>
        translation.title.trim() || translation.description.trim(),
    );
  const isValid =
    configuredTranslations.length > 0 &&
    configuredTranslations.every(
      (translation) =>
        translation.title.trim() && translation.description.trim(),
    );
  const activeHasAnyValue = Boolean(
    activeTranslation.title.trim() || activeTranslation.description.trim(),
  );
  const showRequiredForEmpty =
    hasAttemptedSave && configuredTranslations.length === 0;
  const hasTitleError =
    hasAttemptedSave &&
    ((activeHasAnyValue && !activeTranslation.title.trim()) ||
      showRequiredForEmpty);
  const hasDescriptionError =
    hasAttemptedSave &&
    ((activeHasAnyValue && !activeTranslation.description.trim()) ||
      showRequiredForEmpty);

  const handleFieldChange = (field: "title" | "description", value: string) => {
    setTranslations((current) => ({
      ...current,
      [activeLocale]: {
        ...(current[activeLocale] ?? emptyTranslation(activeLocale)),
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    setHasAttemptedSave(true);
    if (!isValid) {
      const invalidTranslation = configuredTranslations.find(
        (translation) =>
          !translation.title.trim() || !translation.description.trim(),
      );
      if (invalidTranslation) setActiveLocale(invalidTranslation.locale);
      return;
    }

    onSave(
      configuredTranslations.map((translation) => ({
        ...translation,
        title: translation.title.trim(),
        description: translation.description.trim(),
      })),
      isAvailable,
    );
  };

  return (
    <div
      className={styles.nestedModalBoundary}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Modal onClose={onClose} closeOnOverlayClick={false}>
        <Modal.Header>{t("configureInfoReward")}</Modal.Header>
        <Modal.Tabs
          tabs={availableLocales}
          activeTab={activeLocale}
          onClick={setActiveLocale}
          isSmallModifier
          isLocaleTabs
        />
        <Modal.Content className={styles.infoModalContent}>
          <div className={`bonus-form-box--row ${styles.infoTitleRow}`}>
            <InnerInput
              value={activeTranslation.title}
              placeholder={t("enterTitle")}
              label={t("title")}
              type="text"
              id={`loyalty-info-title-${activeLocale}`}
              onChange={(value) => handleFieldChange("title", value)}
              hasError={hasTitleError}
              isRequired
            />
            <div className="bonus-form-switcher">
              <div className="title">{t("available")}</div>
              <SwitchBtn
                activeTxt={t("available")}
                notActiveTxt={t("unavailable")}
                isActive={isAvailable}
                handleClick={() => setIsAvailable((current) => !current)}
              />
            </div>
          </div>
          <InnerInput
            value={activeTranslation.description}
            placeholder={t("enterText")}
            label={t("description")}
            type="textarea"
            id={`loyalty-info-description-${activeLocale}`}
            onChange={(value) => handleFieldChange("description", value)}
            hasError={hasDescriptionError}
            isRequired
          />
          {hasAttemptedSave && !isValid && (
            <p className={styles.validationMessage}>
              {t("infoRewardTranslationsRequired")}
            </p>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button isSecondary txt={t("cancel")} handleClick={onClose} />
          <Button
            isPrimary
            txt={initialTranslations.length > 0 ? t("save") : t("add")}
            handleClick={handleSave}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LoyaltyRewardInfoModal;
